/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { UserCog, Search, Stethoscope, User, Headset, Undo2, VenetianMask } from 'lucide-react';
import logger from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { value: 'doctor', label: 'Dottore', icon: Stethoscope },
  { value: 'patient', label: 'Paziente', icon: User },
  { value: 'operator', label: 'Operatore', icon: Headset },
];

const ROLE_LABELS = { doctor: 'Dottore', patient: 'Paziente', operator: 'Operatore', admin: 'Admin' };

const homeForRole = (role) => {
  if (role === 'doctor') return '/area-dottore';
  if (role === 'operator') return '/backoffice';
  return '/';
};

// ── Dialog di selezione ruolo + utente (per admin nella top bar) ──
export function ImpersonateDialog({ open, onOpenChange }) {
  const { impersonate } = useAuth();
  const [role, setRole] = useState('doctor');
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);

  const fetchUsers = useCallback(async (r, q) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/patients`, { params: { role: r, search: q || undefined } });
      setUsers(res.data);
    } catch (err) {
      logger.error('Fetch users error:', err);
      setUsers([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchUsers(role, search), 300);
    return () => clearTimeout(t);
  }, [open, role, search, fetchUsers]);

  useEffect(() => {
    if (open) { setSearch(''); }
  }, [open, role]);

  const handleImpersonate = async (u) => {
    setSwitching(true);
    try {
      const newUser = await impersonate(u.id);
      onOpenChange(false);
      toast.success(`Ora stai operando come ${newUser.nome} ${newUser.cognome} (${ROLE_LABELS[newUser.role] || newUser.role})`);
      // Navigazione completa: garantisce che tutta l'app riparta con la nuova identità
      window.location.assign(homeForRole(newUser.role));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel cambio utente');
      setSwitching(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{fontFamily:'Space Grotesk'}}>
            <VenetianMask size={18} className="text-[#238A92]" /> Cambia ruolo
          </DialogTitle>
          <DialogDescription>
            Scegli il ruolo e poi l'utente: opererai come lui senza rifare il login. Potrai tornare admin in ogni momento.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={role} onValueChange={setRole}>
          <TabsList className="grid grid-cols-3 w-full">
            {ROLES.map(r => {
              const Icon = r.icon;
              return (
                <TabsTrigger key={r.value} value={r.value} data-testid={`impersonate-role-${r.value}`}>
                  <Icon size={14} className="mr-1.5" /> {r.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>

        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Cerca per nome, cognome o email..."
            className="pl-8"
            data-testid="impersonate-search-input"
          />
        </div>

        <div className="border rounded-lg overflow-y-auto flex-1 min-h-48 max-h-72" data-testid="impersonate-users-list">
          {loading ? (
            <div className="p-3 space-y-2">{[1,2,3,4].map(i => <Skeleton key={i} className="h-10 rounded" />)}</div>
          ) : users.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10" data-testid="impersonate-empty">
              Nessun utente trovato con questo ruolo
            </p>
          ) : (
            users.map(u => (
              <button
                key={u.id}
                type="button"
                disabled={switching}
                onClick={() => handleImpersonate(u)}
                className="w-full flex items-center justify-between gap-3 px-3 py-2.5 border-b last:border-b-0 text-left hover:bg-[hsl(var(--secondary))] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#238A92] disabled:opacity-50"
                data-testid={`impersonate-user-${u.id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{u.nome} {u.cognome}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{u.email}</p>
                </div>
                <Badge variant="secondary" className="text-[10px] flex-shrink-0">{ROLE_LABELS[u.role] || u.role}</Badge>
              </button>
            ))
          )}
        </div>
        <p className="text-xs text-muted-foreground">{users.length} {users.length === 1 ? 'utente' : 'utenti'}</p>
      </DialogContent>
    </Dialog>
  );
}

// ── Pulsante top bar (visibile solo all'admin non in impersonificazione) ──
export function ImpersonateButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!user || user.role !== 'admin' || user.impersonator_email) return null;
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-8 text-[#238A92] border-[#238A92]/40 hover:bg-[#238A92]/10"
        onClick={() => setOpen(true)}
        data-testid="impersonate-open-btn"
      >
        <UserCog size={15} className="mr-1.5" /> Cambia ruolo
      </Button>
      <ImpersonateDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

// ── Banner globale: visibile su tutte le pagine durante l'impersonificazione ──
export function ImpersonationBanner() {
  const { user, stopImpersonation } = useAuth();
  const [restoring, setRestoring] = useState(false);

  if (!user?.impersonator_email) return null;

  const handleStop = async () => {
    setRestoring(true);
    try {
      await stopImpersonation();
      toast.success('Sei tornato admin');
      // Navigazione completa: ripulisce lo stato dell'utente impersonato
      window.location.assign('/backoffice');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel ripristino della sessione admin');
      setRestoring(false);
    }
  };

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-[300] bg-[#1F2A37] text-white px-4 py-2.5 flex items-center justify-center gap-3 flex-wrap shadow-lg"
      data-testid="impersonation-banner"
    >
      <VenetianMask size={16} className="text-amber-400 flex-shrink-0" />
      <p className="text-sm">
        Stai impersonando <strong>{user.nome} {user.cognome}</strong>
        <span className="text-white/70"> ({ROLE_LABELS[user.role] || user.role} — {user.email})</span>
      </p>
      <Button
        size="sm"
        onClick={handleStop}
        disabled={restoring}
        className="h-7 bg-amber-500 hover:bg-amber-600 text-[#1F2A37] font-semibold"
        data-testid="impersonation-stop-btn"
      >
        <Undo2 size={14} className="mr-1" /> {restoring ? 'Ripristino...' : 'Torna Admin'}
      </Button>
    </div>
  );
}
